"use client";

import {
  formatMobileForDisplay,
  getSessionData,
  redirectWithUtmParam,
} from "@repo/shared-utils/common-utils";
import Conditional from "@repo/shared-ui/Conditional";
import BFLModal from "@repo/shared-ui/BFLModal";
import { useState, useEffect } from "react";
import {
  useAppSelector,
  selectIsAuthSuccessOrPerpetual,
  selectLeadStatus,
  selectLoginStatus,
  useAppDispatch,
  setLeadStatus,
  LeadStatus,
} from "@repo/global-store";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import { consentInsert, consentRead } from "@repo/sso";
import { leadsBuySell } from "@repo/sso/leadsApi";

import BFLOtpSendModal from "@repo/sso/BFLOtpSendModal";
import BFLOtpVerifyModal from "@repo/sso/BFLOtpVerifyModal";
import BFLConsentModal from "@repo/sso/BFLConsentModal";
import FlowButton from "@repo/shared-ui/FlowButton";
import { CombinedClevertapProps, dataLayer } from "@repo/analytics/data-layer-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { Heading } from "../../interfaces/IAMCContent.interface";
import { buildInvestNowRedirectUrl } from "../../utils/buildInvestNowRedirectUrl";
import { recordAmcPdpCtaMetric } from "../../monitoring/amcPdpMonitoringHelpers";

type ConsentInsertResponse = {
  statusCode?: number;
  status?: string;
  message?: string;
  data?: {
    mobileNumber?: string | number;
  };
};
export const submitConsentInsert = async (): Promise<ConsentInsertResponse> => {
  const consentResponse = (await consentInsert(
    "BFSL",
    "BFL Entity",
    true
  )) as ConsentInsertResponse;
  return consentResponse;
};

const InvestNowFlow = ({
  amcName,
  headingContent,
  isin,
  bseSchCode,
}: {
  amcName?: string;
  headingContent?: Heading;
  isin?: string;
  bseSchCode?: string;
}) => {
  const ConsentModal = {
    title: "Redirecting you to Bajaj Financial Securities Limited",
    link: "https://cms-assets.bajajfinserv.in/is/content/bajajfinance/BFSL_Terms_Conditionspdf-1",
    openInNewTab: "false",
    note: "Note",
    para1:
      "To provide you with the best trading solutions, we are sharing your name and mobile number with Bajaj Financial Securities Limited. This will help you log in instantly.",
    para2:
      "Click on CONTINUE to share your details for auto login. If you choose to skip it, you will be asked to log in by entering your credentials on Bajaj Financial Securities Limited.",
    list: [
      {
        item0: {
          text: "The usage of your information is governed basis the terms and conditions",
        },
        item1: {
          text: "Bajaj Financial Securities Limited will share your relationship and transaction details with Bajaj Finance Limited for displaying such details on web portal/mobile application of BFL.",
        },
        item2: {
          text: "You can revoke your consent on our app or website by going to the Help & Support section, once you&apos;ve completed any required agreements for the product or service (if availed any).",
        },
      },
    ],
  };

  const otpSendModal = {
    title: "Open your free Demat and trading account",
    subtitle: "Kickstart your investment journey today",
    desc: "Powered by Bajaj Financial Securities Limited (Bajaj Broking)",
    getOtp: "GET OTP",
    link: "https://cms-assets.bajajfinserv.in/is/content/bajajfinancestage/BFSL_Terms_Conditionspdf-1",
    openInNewTab: "false",
    number: "Mobile Number",
    sending: "SENDING...",
  };
  const verifyOtp = {
    title: "Verification required",
    subtitle1: "A one-time password has been sent to your mobile number +91 ${mobileNo}",
    subtitle2: "A one-time password has been sent to your registered mobile number",
    desc: "Powered by Bajaj Financial Securities Limited (Bajaj Broking)",
    getOtp: "GET OTP",
    link: "https://cms-assets.bajajfinserv.in/is/image/bajajfinance/otp-6?scl=1&fmt=png-alpha",
    openInNewTab: "false",
    resend: "RESEND OTP",
    resendOtp: "Resend OTP in",
    sending: "SENDING...",
    submit: "Submit",
    validating: "VALIDATING...",
  };

  const {
    redirectPTBlabel = "Resume Journey",
    redirectlabel = "Open MF Account",
    redirectETBlabe = "Invest Now",
    redirectlink = "",
    redirectApplink = "",
  } = headingContent ?? {};

  const isAuthSuccessOrPerpetual = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const leadStatus = useAppSelector(selectLeadStatus);
  const loginStatus = useAppSelector(selectLoginStatus);
  const appDispatch = useAppDispatch();

  const [openModal, setOpenModal] = useState<"consent" | "send" | "verify" | "none">("none");
  const [consentProvided, setConsentProvided] = useState<boolean>(false);
  const [sessionMobileNo, setSessionMobileNo] = useState<string | undefined>(undefined);
  const [buttonText, setButtonText] = useState<string | undefined>(redirectlabel);

  const [showStickyCTA, setShowStickyCTA] = useState(false);

  /**
   * getConsentReadData
   */
  const getConsentReadData = async () => {
    try {
      const consentReadData = await consentRead("BFSL", "34", "BFL Entity");
      const hasConsent = consentReadData?.data?.consentValue === "2";
      setConsentProvided(hasConsent);
      return hasConsent;
    } catch {
      setConsentProvided(false);
      return false;
    }
  };

  useEffect(() => {
    if (leadStatus === null) {
      console.warn("Lead status is null");
      setButtonText(redirectlabel);
      return;
    }
    getConsentReadData();
    if (leadStatus === "Inprogress") {
      setButtonText(redirectPTBlabel);
    } else if (leadStatus === "ETB") {
      setButtonText(redirectETBlabe);
    } else {
      setButtonText(redirectlabel);
    }
  }, [leadStatus, redirectETBlabe, redirectPTBlabel, redirectlabel]);

  useEffect(() => {
    if (!isAuthSuccessOrPerpetual) {
      console.warn("Not fetching lead status as user is not logged in or perpetual");
      return;
    }

    const getLeadsData = async () => {
      const { mobileNo } = await getSessionData();

      if (mobileNo) {
        const leadsData = await leadsBuySell(mobileNo);

        if (leadsData?.statusCode === 90) {
          appDispatch(
            setLeadStatus({
              leadStatus: leadsData.data?.flag as LeadStatus,
            })
          );
        } else {
          appDispatch(
            setLeadStatus({
              leadStatus: "" as LeadStatus,
            })
          );
        }
      }
    };

    getLeadsData();
  }, [appDispatch, isAuthSuccessOrPerpetual]);

  // Get mobile number from session data
  useEffect(() => {
    let hasMounted = true;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchSessionMobileNumber = async (rc = 0) => {
      try {
        const { mobileNo } = await getSessionData();
        if (mobileNo) {
          if (hasMounted) setSessionMobileNo(mobileNo);
        } else if (rc < 10 && hasMounted) {
          retryTimeoutId = setTimeout(() => fetchSessionMobileNumber(rc + 1), 500);
        }
      } catch (e) {
        if (rc < 10 && hasMounted) {
          retryTimeoutId = setTimeout(() => fetchSessionMobileNumber(rc + 1), 500);
        } else {
          console.warn("Error fetching session data:", e);
        }
      }
    };

    fetchSessionMobileNumber();

    return () => {
      hasMounted = false;
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
    };
  }, []);

  useEffect(() => {
    const heading = document.getElementById("Heading");
    if (!heading) return;

    const handleScroll = () => {
      setShowStickyCTA(heading.getBoundingClientRect().bottom < 0);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleOtpValidationSuccess = () => {
    recordAmcPdpCtaMetric("otp_verified", {
      componentName: "InvestNowFlow",
      ctaName: "Invest Now",
      success: true,
    });
    setOpenModal("none");
  };

  const handleOtpValidationFailure = () => {
    // TODO - Keep modal open for retry
  };

  const handleCTAClick = async (label: string = "") => {
    const payload = {
      component: "amc_heading_bottom_cta",
      element: "cta_strip_card_button_click",
      event: "click",
      event_type: "button_click",
      destination_url: "",
      section_title: amcName,
      cta_text: label,
      product_name: "bajaj_mutual_fund",
      category_name: "loan",
      page_type: "pdp",
      customer_tag: loginStatus,
      is_loggedin: loginStatus,
      component_position: "",
      component_id: `{${amcName}/similar_funds`,
    };
    await dataLayer(loginStatus, payload, payload as unknown as CombinedClevertapProps);
  };

  const getModalToOpen = async () => {
    if (consentProvided) {
      return "send";
    }

    const hasExistingConsent = await getConsentReadData();
    return hasExistingConsent ? "send" : "consent";
  };

  const redirectToDefaultJourney = () => {
    redirectWithUtmParam(isOpenInApp() ? redirectApplink : redirectlink, "false");
  };

  const redirectToInvestNow = async () => {
    const mobileNo = sessionMobileNo ?? (await getSessionData()).mobileNo;

    const investNowUrl = buildInvestNowRedirectUrl({
      isin: isin || "",
      bseSchCode: bseSchCode || "",
      mobileNo: mobileNo || "",
    });
    // Same encrypted trading-mall URL for web and app
    redirectWithUtmParam(investNowUrl, "false");
  };

  return (
    <>
      <Conditional
        if={(isAuthSuccessOrPerpetual && leadStatus === null) || loginStatus === "pending"}
      >
        <BFLSkeleton width={225} count={1} height={40} />
      </Conditional>

      <Conditional
        if={
          (isAuthSuccessOrPerpetual &&
            (leadStatus === "" || leadStatus === "null" || leadStatus === "Inprogress")) ||
          loginStatus === "failure"
        }
      >
        <FlowButton
          buttonText={buttonText}
          onClick={async () => {
            await handleCTAClick(buttonText);
            if (!isAuthSuccessOrPerpetual) {
              recordAmcPdpCtaMetric("invest_redirect", {
                componentName: "InvestNowFlow",
                ctaName: buttonText,
                success: true,
                extra: { flow_step: "guest_redirect" },
              });
              redirectToDefaultJourney();
              return;
            }

            const modalToOpen = await getModalToOpen();
            recordAmcPdpCtaMetric("modal_opened", {
              componentName: "InvestNowFlow",
              ctaName: buttonText,
              success: true,
              extra: { modal_step: modalToOpen },
            });
            setOpenModal(modalToOpen);
          }}
        />
      </Conditional>

      {/* Invest Now */}
      <Conditional if={isAuthSuccessOrPerpetual && leadStatus === "ETB"}>
        <FlowButton
          buttonText={buttonText}
          onClick={async () => {
            await handleCTAClick(buttonText);
            recordAmcPdpCtaMetric("invest_redirect", {
              componentName: "InvestNowFlow",
              ctaName: redirectlabel,
              success: true,
              extra: { flow_step: "etb_redirect" },
            });
            await redirectToInvestNow();
          }}
        />
      </Conditional>

      {showStickyCTA && (
        <div className="fixed right-0 bottom-0 z-[6] p-3 md:hidden">
          <FlowButton
            buttonText={
              isAuthSuccessOrPerpetual && leadStatus === "ETB" ? redirectlabel : buttonText
            }
            onClick={async () => {
              const label =
                isAuthSuccessOrPerpetual && leadStatus === "ETB" ? redirectlabel : buttonText;

              await handleCTAClick(label);

              if (leadStatus === "ETB") {
                await redirectToInvestNow();
                return;
              }

              if (leadStatus !== "Inprogress") {
                recordAmcPdpCtaMetric("invest_redirect", {
                  componentName: "InvestNowFlow",
                  ctaName: label,
                  success: true,
                  extra: { flow_step: "sticky_redirect" },
                });
                redirectToDefaultJourney();
                return;
              }

              const modalToOpen = await getModalToOpen();
              recordAmcPdpCtaMetric("modal_opened", {
                componentName: "InvestNowFlow",
                ctaName: label,
                success: true,
                extra: { modal_step: modalToOpen, flow_step: "sticky_cta" },
              });
              setOpenModal(modalToOpen);
            }}
          />
        </div>
      )}
      <Conditional if={openModal === "consent"}>
        <BFLModal
          data-testid="bfl-modal"
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
          }}
          title="Redirecting you to Bajaj Financial Securities Limited"
          secondaryButtonText="Skip"
          onSecondaryButtonClick={() => {
            setOpenModal("none");
            recordAmcPdpCtaMetric("consent_skipped", {
              componentName: "InvestNowFlow",
              ctaName: "Skip",
              success: true,
              extra: { flow_step: "consent_skip_redirect" },
            });
            redirectToDefaultJourney();
          }}
          onPrimaryButtonClick={async () => {
            try {
              const consentResponse = await submitConsentInsert();

              if (consentResponse.statusCode === 90 && consentResponse.status === "success") {
                setConsentProvided(true);
                const mobileNumber = consentResponse.data?.mobileNumber;
                if (typeof mobileNumber === "string" || typeof mobileNumber === "number") {
                  setSessionMobileNo(String(mobileNumber));
                }
                recordAmcPdpCtaMetric("consent_accepted", {
                  componentName: "InvestNowFlow",
                  ctaName: "Continue",
                  success: true,
                });
                setOpenModal("send");
              } else {
                console.error("Consent API failed:", consentResponse.message);
                setConsentProvided(false);
                recordAmcPdpCtaMetric("consent_failed", {
                  componentName: "InvestNowFlow",
                  ctaName: "Continue",
                  success: false,
                });
              }
            } catch (error) {
              console.error("Error in consent submission:", error);
              setConsentProvided(false);
              recordAmcPdpCtaMetric("consent_failed", {
                componentName: "InvestNowFlow",
                ctaName: "Continue",
                success: false,
              });
            }
          }}
          enableScroll={false}
          buttonText="Continue"
        >
          <BFLConsentModal ssoContent={ConsentModal} />
        </BFLModal>
      </Conditional>

      <Conditional if={openModal === "send"}>
        <BFLModal
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
          }}
          onPrimaryButtonClick={() => {
            recordAmcPdpCtaMetric("otp_send_continue", {
              componentName: "InvestNowFlow",
              ctaName: "Continue",
              success: true,
              extra: { modal_step: "send" },
            });
            setOpenModal("verify");
          }}
          title={otpSendModal?.title}
          enableScroll={false}
          subtitle={otpSendModal?.subtitle}
        >
          <BFLOtpSendModal
            ssoContent={otpSendModal}
            setOpenModal={setOpenModal}
            prefillMobileNumber={sessionMobileNo}
          />
        </BFLModal>
      </Conditional>

      <Conditional if={openModal === "verify"}>
        <BFLModal
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
          }}
          title={verifyOtp?.title}
          subtitle={
            sessionMobileNo
              ? verifyOtp?.subtitle1?.replace(
                  "${mobileNo}",
                  formatMobileForDisplay(sessionMobileNo)
                )
              : verifyOtp?.subtitle2
          }
        >
          <BFLOtpVerifyModal
            ssoContent={verifyOtp}
            onValidationSuccess={handleOtpValidationSuccess}
            onValidationFailure={handleOtpValidationFailure}
            mobileNumber={sessionMobileNo}
          />
        </BFLModal>
      </Conditional>
    </>
  );
};

export default InvestNowFlow;
